<template>
  <AnalysisPage title="目標設定">
    <template #action>
      <Button :primary="noData" small @click="settingModal = true" :disabled="!isMounted">
        {{ noData ? '設定' : '変更' }}
      </Button>
    </template>
    <AnalysisContent :order="['table', 'graph', 'action']" :noSet="isMounted && noData">
      <template #table>
        <TargetTable />
      </template>
      <template #graph>
        <LoadingIndicator v-if="busy" />
        <div v-else class="target">
          <div class="target-chart" ref="chart">
            <div class="title">日別目標</div>
            <FrameTemplate :nodata="!dayChart">
              <template #v-axis-unit>人</template>
              <Chart type="AreaChart" :data="dayChart" :options="{ ...options }" />
              <template #h-axis-unit>
                <p>日</p>
                <p>曜日</p>
              </template>
            </FrameTemplate>
          </div>
          <div class="target-chart">
            <div class="title">積上目標</div>
            <FrameTemplate :nodata="!stackChart">
              <template #v-axis-unit>人</template>
              <Chart type="AreaChart" :data="stackChart" :options="{ ...options }" />
              <template #h-axis-unit>
                <p>日</p>
                <p>曜日</p>
              </template>
            </FrameTemplate>
          </div>
        </div>
      </template>
      <template #action>
        <Button small @click="release" class="release-button">目標設定を解除</Button>
      </template>
      <template #no-set>目標が未設定です<br />目標設定ページで設定してください</template>
    </AnalysisContent>
    <TargetSettingModal v-if="settingModal" @close="settingModal = false" @fetch="fetch" :setting="setting" />
  </AnalysisPage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Button from '@/components/atoms/Button'
import AnalysisPage from '@/components/templates/AnalysisPage'
import AnalysisContent from '@/components/molecules/AnalysisContent'
import Chart, { FrameTemplate } from '@/components/molecules/Chart'
import Dialog from '@/components/molecules/ModalDialog'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import TargetTable from './Components/TargetTable'
import TargetSettingModal from './Components/TargetSettingModal'

export default {
  name: 'AnalysisTarget',
  components: {
    Button,
    AnalysisPage,
    AnalysisContent,
    TargetTable,
    Chart,
    LoadingIndicator,
    FrameTemplate,
    TargetSettingModal,
  },
  data() {
    return {
      isMounted: false,
      settingModal: false,
      chartWitch: 352,
    }
  },
  computed: {
    ...mapState({
      busy: state => state.analysis.target.busy,
      type: state => state.analysis.target.type,
    }),
    ...mapGetters({
      dayChart: 'analysis/target/dayChart',
      stackChart: 'analysis/target/stackChart',
      setting: 'analysis/target/setting',
      colors: 'analysis/colors/target',
    }),
    options() {
      return {
        colors: this.colors.filter((color, i) => i === 1),
        chartArea: {
          height: 208,
          top: 6,
        },
        definitions: { role: 'style' },
        height: 252,
        hAxis: {
          showTextEvery: this.chartWitch > 352 ? 2 : 3,
        },
        series: {
          0: {
            areaOpacity: 0.6,
            pointSize: 0,
          },
        },
        legend: { position: 'none' },
      }
    },
    noData() {
      return !this.dayChart && !this.stackChart
    },
  },
  async mounted() {
    await this.fetch()
    this.isMounted = true
    window.addEventListener('resize', this.windowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.windowResize)
  },
  methods: {
    async fetch() {
      const { dispatch } = this.$store
      await dispatch('analysis/target/get')
    },
    async release() {
      if (
        !(await Dialog.confirm({
          title: `目標設定を解除`,
          message: `設定した目標人数を解除してよろしいですか？`,
          okText: '解除',
          cancelText: '戻る',
          description: '※解除後は目標未設定となり、設定した数値は破棄されます',
          caution: true,
        }))
      ) {
        return
      }
      if (await this.$store.dispatch('analysis/target/saveRelease')) {
        this.fetch()
      }
    },
    windowResize() {
      this.chartWitch = this.$refs.chart.offsetWidth
    },
  },
}
</script>

<style scoped lang="postcss">
>>> .content > .graph {
  min-height: 352px;
  padding-top: 28px;
}

.title {
  font-weight: bold;
  margin-bottom: 8px;
}

.target {
  display: grid;
  grid-auto-flow: column;
  grid-gap: 24px;
  grid-template-columns: 1fr 1fr;
}

.target-chart {
  >>> .layout {
    grid-template-columns: 48px 1fr 24px;
    height: 268px;
  }

  .v-axis-unit {
    font-size: 11px;
    line-height: 1.3;
    margin-left: 28px;
    position: relative;
    z-index: 2;
  }

  >>> .h-axis-unit {
    align-items: normal;
    flex-direction: column;
    line-height: 1.3;
    margin: auto 0 4px;
  }
}

.release-button {
  width: 152px;
}
</style>
